import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ConfirmationModal, Text } from '@frontend/design-system';
import {
  FilterExportWarning,
  useInvoiceExportModalControl,
} from '../../../hooks/invoices/use-invoice-export-modal-control';
import { NumberedRow } from '../../NumberedRow';

type InvoiceExportModalProps = Omit<ReturnType<typeof useInvoiceExportModalControl>, 'showInvoiceExportWarning'> & {
  onConfirm: () => void;
};

export const InvoiceExportModal = ({ modalProps, exportWarning, onConfirm }: InvoiceExportModalProps) => {
  const { t } = useTranslation('payments');

  const warningsCount = useMemo(() => Object.values(exportWarning).filter(Boolean).length, [exportWarning]);

  const textMap: Record<keyof FilterExportWarning, string> = useMemo(() => {
    return {
      searchParams: t('The Search filter option is not currently supported on this export.'),
      truncateDateRange: t(
        'To enhance export speed, the data will be limited to the last 3 months of data you’ve requested.'
      ),
    };
  }, []);

  return (
    <ConfirmationModal
      {...modalProps}
      title={t('Please Note')}
      confirmLabel={t('Continue')}
      onConfirm={() => {
        modalProps.onClose();
        onConfirm();
      }}
    >
      <div
        css={css`
          margin-bottom: ${theme.spacing(2)};
        `}
      >
        {Object.entries(exportWarning).map(([key, value], idx) => {
          const text = textMap[key as keyof FilterExportWarning];
          if (!value || !text) {
            return null;
          }
          return (
            <div key={idx}>
              {warningsCount > 1 && (
                <NumberedRow number={idx + 1}>
                  <Text>{text}</Text>
                </NumberedRow>
              )}
              {warningsCount <= 1 && <Text>{text}</Text>}
            </div>
          );
        })}
      </div>
    </ConfirmationModal>
  );
};
